import { useEffect, useState } from "react";
import {
  FormControlLabel,
  Grid,
  Switch,
  Box,
  DialogContent,
  DialogActions,
  Button,
  Autocomplete,
  TextField,
} from "@mui/material";
import GenericTextField from "../../components/GenericTextField";
import GenericDatePicker from "../../components/GenericDatePicker";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import latinize from "latinize";
import styles from "./styles.module.css";
import dayjs from "dayjs";
import { buscarGruposUsuarios } from "../../services/api-autenticacao.service";

const UsuarioFormSchema = z
  .object({
    idUsuario: z.number().readonly(),
    nomeCompleto: z.string().min(1, {
      message: "Nome completo é obrigatório",
    }),
    ativo: z.boolean(),
    login: z.string().min(1, {
      message: "Login é obrigatório",
    }),
    email: z.string().min(1, {
      message: "Email é obrigatório",
    }),
    dtExpiracao: z.date(),
    senha: z.string(),
    confirmarSenha: z.string(),
    idGrupoUsuario: z.number()
  })
  .partial({
    idUsuario: true,
    senha: true,
    confirmarSenha: true,
  })
  .required({
    nomeCompleto: true,
    login: true,
    email: true,
    dtExpiracao: true,
    idGrupoUsuario: true
  })
  .refine((data) => data.senha === data.confirmarSenha, {
    message: "As senhas não são idênticas",
    path: ["confirmarSenha"],
  })
  .refine(
    (data) =>
      String(data.email)
        .toLowerCase()
        .match(
          /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|.(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
        ),
    {
      message: "Digite um e-mail válido",
      path: ["email"],
    }
  )
  .superRefine(({ idUsuario, senha }, ctx) => {
    if (idUsuario === undefined && senha) {
      var reasonMsg = "";
      if (!/(?=^.{10,}$).*$/.test(senha))
        reasonMsg += "\n- Ao menos 10 caracteres;";
      if (!/(?=.*[a-z]).*$/.test(senha))
        reasonMsg += "\n- Uma letra minuscula (a-z);";
      if (!/(?=.*[A-Z]).*$/.test(senha))
        reasonMsg += "\n- Uma letra maiúscula (A-Z);";
      if (!/(?=.*\d).*$/.test(senha)) reasonMsg += "\n- Um digito (0-9);";
      if (!/(?=.*[!@#$%^&*]+).*$/.test(senha))
        reasonMsg += "\n- Um caractere especial (!@#$%^&*);";
      if (
        !/(?=^.{10,}$)(?=.*[a-z])(?=.*[A-Z])(?=.*[!@#$%^&*]+)(?=.*\d).*$/.test(
          senha
        )
      ) {
        ctx.addIssue({
          code: "custom",
          message: "A senha deve conter" + reasonMsg,
          path: ["senha"],
        });
      }
    }
  });

type UserFormData = z.infer<typeof UsuarioFormSchema>;

type UserFormProps = {
  userValues: UserFormData;
  userPermissionData: any;
  userPermissionValues: any;
  loading: boolean;
  onSubmit(data: UserFormData): void;
  onClose(event: any): void;
};

export default function UsuarioForm({
  userValues,
  onSubmit,
  onClose,
}: UserFormProps) {
  const [options, setOptions] = useState([])
  const [optionSelected, setOptionSelected] = useState(null)

  useEffect(() => {
    const fetchListaGrupos = async () => {
      try {
        const response = await buscarGruposUsuarios()
        const listaFilter = response.grupos.filter(item => item.ativo === true) 
        const lista = listaFilter.map((item): any => { return { id: item.idGrupoUsuario, label: item.descricao } })
        setOptions(lista);

      } catch (error) {
        console.log(error);
      }
    }
    fetchListaGrupos()
  }, [])

  if (userValues.dtExpiracao === undefined) {
    userValues.dtExpiracao = dayjs().subtract(1, "day").toDate();
  }

  const {
    control,
    handleSubmit,
    formState: { errors },
    setValue,
  } = useForm<UserFormData>({
    mode: "onChange",
    resolver: zodResolver(UsuarioFormSchema),
    defaultValues: {
      idUsuario: userValues.idUsuario || undefined,
      nomeCompleto: userValues.nomeCompleto || "",
      ativo: userValues.ativo || false,
      login: userValues.login || "",
      email: userValues.email || "",
      dtExpiracao:
        userValues.dtExpiracao || dayjs().subtract(1, "day").toDate(),
      senha: "",
      confirmarSenha: "",
      idGrupoUsuario: userValues.idGrupoUsuario
    },
  });

  const handleChangeAutoComplete = (value: any) => {
    const optionSelected = options.find(item => item.label === value)
    setValue("idGrupoUsuario", optionSelected?.idGrupoUsuario)
    setOptionSelected(value)
  }

  const handleCloseForm = (event: any) => {
    onClose(event);
  };

  const handleNomeCompletoChange = (event: any) => {
    const nomeCompletoValue = event.target.value;

    const suggestedLoginNameArr = latinize(nomeCompletoValue)
      .toLowerCase()
      .replace(/\s+/g, ".")
      .split(".");

    const suggestedLoginName =
      suggestedLoginNameArr[0] +
      (suggestedLoginNameArr[1]
        ? suggestedLoginNameArr[2] && suggestedLoginNameArr[1].length <= 2
          ? "." + suggestedLoginNameArr[2]
          : "." + suggestedLoginNameArr[1]
        : "");

    setValue("login", suggestedLoginName, { shouldValidate: true });
  };

  const handleLoginChange = (event: any) => {
    const loginValue = event.target.value;

    const adjustedLoginName = latinize(loginValue)
      .toLowerCase()
      .replaceAll(/\.*\s+/g, ".")
      .replaceAll(/\.+/g, ".")
      .replaceAll(/[^a-z\._]/g, "");

    setValue("login", adjustedLoginName, { shouldValidate: true });
  };

  const submit = (data: UserFormData) => {
    const body: any = {
      usuario: {
        login: data.login,
        senha: data.senha,
        nomeCompleto: data.nomeCompleto,
        email: data.email,
        dtExpiracao: dayjs(data.dtExpiracao).format("YYYY-MM-DD"),
        ativo: data.ativo,
        idGrupoUsuario: data.idGrupoUsuario
      },
    };

    onSubmit(body);
  };

  return (
    <form onSubmit={handleSubmit(submit)} className={styles.formDiv}>
      <DialogContent className={styles.formContainer}>
        <Box className={styles.formContent}>
            <Grid container spacing={2}>
              <Grid item xs={2}>
                <Controller
                  name="idUsuario"
                  control={control}
                  defaultValue={userValues.idUsuario}
                  render={({ field: { ref, ...field } }) => (
                    <GenericTextField
                      disabled
                      label="ID"
                      error={Boolean(errors.idUsuario)}
                      helperText={errors.idUsuario?.message}
                      placeholder={"Novo usuário"}
                      inputRef={ref}
                      {...field}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={9}>
                <Controller
                  name="nomeCompleto"
                  control={control}
                  defaultValue={userValues.nomeCompleto}
                  render={({ field: { ref, onChange, ...field } }) => (
                    <GenericTextField
                      required
                      label="Nome Completo"
                      placeholder="Digite seu nome completo"
                      error={Boolean(errors.nomeCompleto)}
                      onChange={(data: any) => {
                        onChange(data);
                        handleNomeCompletoChange(data);
                      }}
                      helperText={errors.nomeCompleto?.message}
                      inputRef={ref}
                      {...field}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={1}>
                <Controller
                  name="ativo"
                  control={control}
                  defaultValue={userValues.ativo}
                  render={({ field: { ref, ...field } }) => (
                    <FormControlLabel
                      control={<Switch color="primary" />}
                      label="Ativo"
                      labelPlacement="start"
                      checked={field.value}
                      sx={{
                        margin: 0,
                      }}
                      inputRef={ref}
                      {...field}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={3}>
                <Controller
                  name="login"
                  control={control}
                  defaultValue={userValues.login}
                  render={({ field: { ref, onChange, ...field } }) => (
                    <GenericTextField
                      required
                      label="Login"
                      placeholder="Digite o seu login"
                      error={Boolean(errors.login)}
                      helperText={errors.login?.message}
                      onChange={(data: any) => {
                        onChange(data);
                        handleLoginChange(data);
                      }}
                      inputRef={ref}
                      {...field}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={4}>
                <Controller
                  name="email"
                  control={control}
                  defaultValue={userValues.email}
                  render={({ field: { ref, ...field } }) => (
                    <GenericTextField
                      required
                      label="Email"
                      placeholder="Digite o seu email"
                      error={Boolean(errors.email)}
                      helperText={errors.email?.message}
                      inputRef={ref}
                      {...field}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={2}>
                <Controller
                  name="dtExpiracao"
                  control={control}
                  defaultValue={userValues.dtExpiracao}
                  render={({ field: { ref, ...field } }) => (
                    <GenericDatePicker
                      label="Data de Expiração"
                      placeholder="Selecione a data de expiração"
                      error={Boolean(errors.dtExpiracao)}
                      helperText={errors.dtExpiracao?.message}
                      inputRef={ref}
                      {...field}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={3}>
                <Controller
                  name="idGrupoUsuario"
                  control={control}
                  render={({ field: { ref, onChange, ...field } }) => (
                    <Autocomplete
                      {...field}
                      options={options}
                      getOptionLabel={(option) => option.label}
                      value={(optionSelected ? optionSelected : options.find(item => item.id === userValues.idGrupoUsuario))|| null}
                      onChange={(_, value) => {
                        handleChangeAutoComplete(value);
                        onChange(value ? value.id : null); // Atualize o valor do Controller
                      }}
                      isOptionEqualToValue={(option, value) => option.id === value.id}
                      renderInput={(params) => <TextField {...params} label="Grupo de Usuario" />}
                      size="small"
                    />
                  )}
                />
              </Grid>
              {!userValues.idUsuario && (
                <>
                  <Grid item xs={6}>
                    <Controller
                      name="senha"
                      control={control}
                      defaultValue={userValues.senha}
                      render={({ field: { ref, ...field } }) => (
                        <GenericTextField
                          required
                          label="Senha"
                          type="password"
                          placeholder="Digite sua senha"
                          error={Boolean(errors.senha)}
                          helperText={
                            <span style={{ whiteSpace: "pre-wrap" }}>
                              {errors.senha?.message}
                            </span>
                          }
                          inputRef={ref}
                          {...field}
                        />
                      )}
                    />
                  </Grid>

                  <Grid item xs={6}>
                    <Controller
                      name="confirmarSenha"
                      control={control}
                      defaultValue={userValues.confirmarSenha}
                      render={({ field: { ref, ...field } }) => (
                        <GenericTextField
                          required
                          label="Repetir Senha"
                          type="password"
                          placeholder="Digite sua senha novamente"
                          error={Boolean(errors.confirmarSenha)}
                          helperText={errors.confirmarSenha?.message}
                          inputRef={ref}
                          {...field}
                        />
                      )}
                    />
                  </Grid>
                </>
              )}
            </Grid>
        </Box>
        <Box>
          <DialogActions className={styles.botoesPopupGeral}>
            <Button
              className={styles.botaoPopupCancelar}
              onClick={handleCloseForm}
              size="small"
            >
              Cancelar
            </Button>
            <Button
              className={styles.botaoPopupSalvar}
              type="submit"
              color="primary"
              size="small"
            >
              Salvar
            </Button>
          </DialogActions>
        </Box>
      </DialogContent>
    </form>
  );
}
